// Seletor de ícone único do app (0.70, texto 09, Parte A).
//
// POR QUE ESTE ARQUIVO EXISTE (a causa do bug do "Mais" em Estudos):
// cada "folha" do app (Sheet) é um <Modal> do React Native. No iPhone, um Modal é apresentado pela
// tela (view controller) em que ele está montado. Se o seletor de ícone fica FORA da folha que está
// aberta (como irmão dela, no fim da tela), o iPhone tenta apresentá-lo pela tela de baixo, que já está
// apresentando a folha: o pedido fica na fila e o seletor só aparece quando a folha fecha (no Salvar).
// Na Nova tarefa funcionava porque o seletor está montado DENTRO da própria folha.
//
// Regra: o seletor é montado junto do botão que o abre. Use <CampoIcone> (ícone tocável + atalhos +
// "Mais") e o seletor sempre abre por cima da folha atual, na hora, em qualquer tela.
import { useMemo, useState, type ReactNode } from 'react';
import { Keyboard, Pressable, ScrollView, Text, View } from 'react-native';
import { CATEGORIAS, buscarIcones } from '../lib/emoji';
import { Chip, Field, Sheet } from './ui';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';
import { ROLAGEM_TECLADO } from './teclado';

type PropsSeletor = { visible: boolean; onPick: (e: string) => void; onClose: () => void; cor: string; atual?: string; extras?: string[] };

/** Tela "Escolher ícone": categorias + busca por nome. Escolher chama onPick e fecha; "Fechar" não muda nada.
 *  Monte-a DENTRO da folha (Sheet) de onde ela é aberta — ou use <CampoIcone>, que já faz isso. */
export function EscolherIcone({ visible, onPick, onClose, cor, atual, extras }: PropsSeletor) {
  const [cat, setCat] = useState(0);
  const [busca, setBusca] = useState('');
  const achados = useMemo(() => buscarIcones(busca, t, extras), [busca, extras]);
  const fechar = () => { setBusca(''); onClose(); };
  const escolher = (e: string) => { setBusca(''); onPick(e); onClose(); };
  const grade = (lista: string[]) => lista.map(e => (
    <Pressable key={e} onPress={() => escolher(e)} accessibilityRole="button"
      style={{ width: '16.66%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center', borderRadius: 14,
        backgroundColor: atual === e ? alpha(cor, 0.3) : 'transparent' }}>
      <Text style={{ fontSize: 34 }}>{e}</Text>
    </Pressable>
  ));
  return (
    <Sheet visible={visible} onClose={fechar} title={t('Escolher ícone')}>
      <View style={{ paddingHorizontal: 16, paddingBottom: 10 }}>
        <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar ícone pelo nome (ex.: livro, casa, café)')} semDitado
          autoCorrect={false} returnKeyType="search" clearButtonMode="while-editing" />
      </View>
      {!busca.trim() && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }} keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ gap: 8, paddingHorizontal: 16, paddingBottom: 12 }}>
          {CATEGORIAS.map(([nome], i) => <Chip key={nome} cor={cor} on={cat === i} label={t(nome)} onPress={() => setCat(i)} />)}
        </ScrollView>
      )}
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag"
        contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 12, paddingBottom: 40 }}>
        {busca.trim()
          ? (achados.length ? grade(achados)
            : <Text style={{ color: C.sub, padding: 16, width: '100%', textAlign: 'center' }}>{t('Nenhum ícone com esse nome. Tente outra palavra ou escolha numa categoria.')}</Text>)
          : grade(CATEGORIAS[cat][1])}
        <View style={{ height: 40, width: '100%' }} />
      </ScrollView>
    </Sheet>
  );
}

type PropsCampo = {
  valor: string; onMudar: (e: string) => void; cor: string;
  /** Atalhos mostrados abaixo (ex.: os 12 do fichário). O último botão, "Mais", abre o seletor completo. */
  rapidos?: string[];
  /** O que fica à direita do ícone (normalmente o campo do nome). */
  children?: ReactNode;
  tam?: number;
};

/**
 * Ícone tocável à esquerda do nome (igual à Nova tarefa) + atalhos opcionais com "Mais".
 * Tocar no ícone ou em "Mais" fecha o teclado e abre "Escolher ícone" NA HORA, por cima da folha atual.
 * Escolher só muda o valor do formulário (não salva); "Fechar" volta sem mudar.
 */
export function CampoIcone({ valor, onMudar, cor, rapidos, children, tam = 56 }: PropsCampo) {
  const [aberto, setAberto] = useState(false);
  const abrir = () => { Keyboard.dismiss(); setAberto(true); };
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Pressable onPress={abrir} accessibilityRole="button" accessibilityLabel={t('Escolher ícone')} hitSlop={6}
          style={{ width: tam, height: tam, borderRadius: Math.round(tam * 0.3), backgroundColor: alpha(cor, 0.18), borderWidth: 2, borderColor: cor,
            alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: Math.round(tam * 0.52) }}>{valor}</Text>
        </Pressable>
        {children != null && <View style={{ flex: 1, minWidth: 0 }}>{children}</View>}
      </View>
      {!!rapidos?.length && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {rapidos.map(ic => (
            <Pressable key={ic} onPress={() => onMudar(ic)} accessibilityRole="button" accessibilityState={{ selected: valor === ic }}
              style={{ width: 46, height: 46, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: valor === ic ? alpha(cor, 0.3) : C.card }}>
              <Text style={{ fontSize: 26 }}>{ic}</Text>
            </Pressable>
          ))}
          <Pressable onPress={abrir} accessibilityRole="button" accessibilityLabel={t('Mais ícones')}
            style={{ height: 46, paddingHorizontal: 12, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: C.card }}>
            <Text style={{ color: C.sub, fontWeight: '700' }}>{t('Mais')}</Text>
          </Pressable>
        </View>
      )}
      {/* Montado aqui, junto do botão: fica dentro da mesma folha e abre por cima dela na hora. */}
      <EscolherIcone visible={aberto} cor={cor} atual={valor} extras={rapidos} onClose={() => setAberto(false)} onPick={onMudar} />
    </View>
  );
}
